import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ArrowLeft } from "lucide-react";
import { apiService } from "../services/api";
import { Application, ApplicationUpdate } from "../types";

export default function EditApplicationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: appData, isLoading } = useQuery(
    ["application", id],
    () => apiService.getApplication(id!),
    {
      enabled: !!id,
    },
  );

  const app: Application | undefined = (appData?.data as any)?.data;

  const [form, setForm] = useState<ApplicationUpdate>({
    name: "",
    description: "",
    applicationType: "",
    environment: "",
    deploymentUrl: "",
    status: "active",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (app) {
      setForm({
        name: app.name,
        description: app.description || "",
        applicationType: app.applicationType || "",
        environment: app.environment || "",
        deploymentUrl: app.deploymentUrl || "",
        status: app.status,
      });
    }
  }, [app]);

  const mutation = useMutation(
    (data: ApplicationUpdate) => apiService.updateApplication(id!, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["application", id]);
        navigate(`/applications/${id}`);
      },
    },
  );

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.name?.trim()) e.name = "Name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const payload: ApplicationUpdate = {};
    if (form.name) payload.name = form.name;
    if (form.description) payload.description = form.description;
    if (form.applicationType) payload.applicationType = form.applicationType;
    if (form.environment) payload.environment = form.environment;
    if (form.deploymentUrl) payload.deploymentUrl = form.deploymentUrl;
    if (form.status) payload.status = form.status;
    mutation.mutate(payload);
  };

  const handleChange = (field: keyof ApplicationUpdate, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!app) {
    return (
      <div className="card p-12 text-center">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Application not found
        </h3>
        <Link
          to="/applications"
          className="mt-4 inline-flex items-center gap-2 text-primary-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Applications
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Link
        to={`/applications/${id}`}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Application
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        Edit Application
      </h1>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            className={`input w-full ${errors.name ? "border-red-500" : ""}`}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={form.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
            rows={3}
            className="input w-full"
          />
        </div>

        {/* Type & Environment row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type
            </label>
            <select
              value={form.applicationType || ""}
              onChange={(e) => handleChange("applicationType", e.target.value)}
              className="input w-full"
            >
              <option value="">Select type...</option>
              <option value="microservice">Microservice</option>
              <option value="api">API</option>
              <option value="web-app">Web App</option>
              <option value="backend">Backend</option>
              <option value="frontend">Frontend</option>
              <option value="worker">Worker</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Environment
            </label>
            <select
              value={form.environment || ""}
              onChange={(e) => handleChange("environment", e.target.value)}
              className="input w-full"
            >
              <option value="">Select environment...</option>
              <option value="production">Production</option>
              <option value="staging">Staging</option>
              <option value="development">Development</option>
              <option value="test">Test</option>
            </select>
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <select
            value={form.status || "active"}
            onChange={(e) => handleChange("status", e.target.value)}
            className="input w-full"
          >
            <option value="active">Active</option>
            <option value="deprecated">Deprecated</option>
            <option value="retired">Retired</option>
          </select>
        </div>

        {/* Deployment URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Deployment URL
          </label>
          <input
            type="url"
            value={form.deploymentUrl || ""}
            onChange={(e) => handleChange("deploymentUrl", e.target.value)}
            className="input w-full"
            placeholder="https://..."
          />
        </div>

        {/* Error message */}
        {mutation.isError && (
          <p className="text-sm text-red-600">
            Failed to update application. Please try again.
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Link to={`/applications/${id}`} className="btn btn-secondary">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={mutation.isLoading}
            className="btn btn-primary"
          >
            {mutation.isLoading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
